import { CHAT_QUEUES } from 'dashboard/routes/dashboard/conversation/chatQueues';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

/**
 * The product areas of the redesigned shell, in rail order.
 *
 * Every entry points at a route that already exists today. Its `activeOn`
 * routes also define which contextual navigation the section sidebar renders.
 * Companies live within Customers.
 *
 * `activeOn` lists the extra route names that should light this section up. A
 * section is active when the current route name equals its `routeName` or
 * appears in `activeOn`.
 */
export const RAIL_SECTIONS = [
  {
    name: 'inbox',
    labelKey: 'RAIL.INBOX',
    icon: 'i-ph-tray',
    activeIcon: 'i-ph-tray-fill',
    routeName: 'home',
    activeOn: [
      'inbox_conversation',
      'conversation_mentions',
      'conversation_participating',
      'conversation_unattended',
      'inbox_dashboard',
      'folder_conversations',
      'conversation_through_inbox',
      'conversations_through_folders',
      'conversation_through_mentions',
      'conversation_through_participating',
      'conversation_through_unattended',
      'conversation_ai_handling',
      'conversation_through_ai_handling',
      'conversation_resolved_by_ai',
      'conversation_through_resolved_by_ai',
      'team_conversations',
      'conversations_through_team',
      'inbox_view',
      'inbox_view_conversation',
      // Tickets is the same queue read ticket-first, so it keeps the Inbox
      // rail entry lit and the Inbox panel beside it.
      'tickets_index',
      'tickets_conversation',
      // Cases (the ticket engine) is work that outlives a conversation, but it
      // is escalated out of the inbox and worked beside it, so it shares the
      // Inbox rail entry and panel rather than owning a rail area of its own.
      // Without these the panel that holds its own nav entry does not render.
      'support_cases_index',
      'support_cases_show',
      // The Chats queues are the same inbox read on a pinned scope, so they
      // keep the Inbox rail entry lit and the Inbox panel beside them.
      ...CHAT_QUEUES.flatMap(({ name }) => [name, `${name}_conversation`]),
    ],
  },
  {
    name: 'calls',
    labelKey: 'RAIL.CALLS',
    icon: 'i-ph-phone',
    activeIcon: 'i-ph-phone-fill',
    routeName: 'calls_dashboard_index',
    // Gated on the same flag as the route; ProductRail additionally hides the
    // entry until an inbox actually has calling switched on, so the section
    // only appears once voice is genuinely in use.
    featureFlag: FEATURE_FLAGS.CHANNEL_VOICE,
  },
  {
    name: 'ai',
    labelKey: 'RAIL.AI',
    icon: 'i-ph-sparkle',
    activeIcon: 'i-ph-sparkle-fill',
    routeName: 'captain_assistants_index',
    // Parent path is `captain/:navigationPath`, so the link is unresolvable
    // without it - vue-router throws during setup and blanks the whole app.
    params: { navigationPath: 'captain_assistants_overview_index' },
    activeOn: [
      'captain_assistants_overview_index',
      'captain_assistants_responses_index',
      'captain_assistants_documents_index',
      'captain_assistants_scenarios_index',
      'captain_assistants_playground_index',
      'captain_assistants_inboxes_index',
      'captain_tools_index',
      'captain_assistants_settings_index',
      'captain_assistants_create_index',
      'captain_assistants_new',
      'captain_assistants_faq_suggestions',
      'captain_assistants_guidelines_index',
      'captain_assistants_guardrails_index',
    ],
  },
  {
    name: 'customers',
    labelKey: 'RAIL.CUSTOMERS',
    icon: 'i-ph-users-three',
    activeIcon: 'i-ph-users-three-fill',
    routeName: 'contacts_dashboard_index',
    // Campaigns live here too: reaching out to customers is the same job as
    // knowing them, so it shares the Customers panel instead of owning a rail
    // entry of its own.
    activeOn: [
      'contacts_dashboard_active',
      'contacts_dashboard_segments_index',
      'contacts_dashboard_labels_index',
      'contacts_edit',
      'contacts_edit_segment',
      'contacts_edit_label',
      'companies_dashboard_index',
      'companies_dashboard_show',
      // Deals are the contacts read as a pipeline, so the board keeps the
      // Customers rail entry lit and the Customers panel beside it.
      'opportunities_index',
      'campaigns_home',
      'campaigns_index',
      'campaigns_channel_index',
      'campaigns_builder',
    ],
  },
  {
    name: 'travel',
    labelKey: 'RAIL.TRAVEL',
    icon: 'i-ph-airplane-tilt',
    activeIcon: 'i-ph-airplane-tilt-fill',
    routeName: 'travel_flight_search',
    // Gated on the same flag as the route, so the rail never offers an entry
    // that opens onto a screen the account cannot reach.
    featureFlag: FEATURE_FLAGS.TRAVEL,
    // Without these the section sidebar does not render on the other two screens.
    activeOn: [
      'travel_booking_lookup',
      'travel_request_history',
      'travel_preferences',
    ],
  },
  {
    name: 'team',
    labelKey: 'RAIL.TEAM',
    icon: 'i-ph-identification-badge',
    activeIcon: 'i-ph-identification-badge-fill',
    routeName: 'team_dashboard_index',
    // Matches the route's own gate, so the rail never offers an entry that
    // opens onto a screen the account member cannot use.
    permissions: ['administrator'],
  },
  {
    name: 'reports',
    labelKey: 'RAIL.REPORTS',
    icon: 'i-ph-chart-bar',
    activeIcon: 'i-ph-chart-bar-fill',
    routeName: 'account_overview_reports',
    activeOn: [
      'conversation_reports',
      'agent_reports_index',
      'agent_reports',
      'agent_reports_show',
      'label_reports_index',
      'label_reports',
      'label_reports_show',
      'inbox_reports_index',
      'inbox_reports',
      'inbox_reports_show',
      'team_reports_index',
      'team_reports',
      'team_reports_show',
      'csat_reports',
      'sla_reports',
      'bot_reports',
    ],
  },
  {
    name: 'settings',
    labelKey: 'RAIL.SETTINGS',
    icon: 'i-ph-gear-six',
    activeIcon: 'i-ph-gear-six-fill',
    // Pinned into the rail's bottom cluster (text.com-style) instead of the
    // scrollable section list. Same route, same activeOn matching.
    pinToBottom: true,
    routeName: 'settings_home',
    activeOn: [
      'general_settings_index',
      'notifications_settings_index',
      'profile_settings',
      'profile_settings_index',
      'profile_settings_mfa',
      'agent_list',
      'settings_teams_list',
      'settings_teams_new',
      'settings_teams_finish',
      'settings_teams_add_agents',
      'settings_teams_show',
      'settings_teams_edit',
      'settings_teams_edit_members',
      'settings_teams_edit_finish',
      'assignment_policy_index',
      'agent_assignment_policy_create',
      'agent_assignment_policy_edit',
      'agent_capacity_policy_create',
      'agent_capacity_policy_edit',
      'labels_wrapper',
      'labels_list',
      'attributes_list',
      'customer_attributes_list',
      'blocked_contacts_list',
      'agent_bots',
      'macros_wrapper',
      'macros_edit',
      'macros_new',
      'canned_list',
      'settings_applications',
      'settings_integrations_dashboard_apps',
      'settings_integrations_webhook',
      'settings_integrations_slack',
      'settings_integrations_linear',
      'settings_integrations_notion',
      'settings_integrations_shopify',
      'settings_applications_integration',
      'settings_data_imports',
      'settings_data_import_show',
      'auditlogs_list',
      'custom_roles_list',
      'sla_wrapper',
      'sla_list',
      'conversation_workflow_index',
      'security_settings_index',
      'billing_settings_index',
      // Tola AI settings render inside the settings shell, so the leaf keeps
      // the settings panel open rather than flipping to the AI area.
      'captain_settings_index',
      // Channels moved off the rail (D-inbox-redesign): reachable from the
      // inbox panel's "Add channel" and here.
      'settings_channel_show',
      'settings_inbox_list',
      'settings_inbox_show',
      'settings_inbox_new',
      'settings_inbox_finish',
      'settings_inboxes_page_channel',
      'settings_inboxes_add_agents',
      // Automation rules. Not the premium conversationWorkflow feature, which
      // is a different thing that happens to share the word.
      'workflows_index',
      'automation_list',
      // Help Center lives under Settings now; the panel group keeps its own
      // feature-flag/permission gate via the destination route meta.
      'portals_index',
      'portals_articles_index',
      'portals_articles_new',
      'portals_articles_edit',
      'portals_categories_index',
      'portals_categories_articles_index',
      'portals_categories_articles_edit',
      'portals_locales_index',
      'portals_settings_index',
    ],
  },
];

export const findRailSectionForRoute = routeName =>
  RAIL_SECTIONS.find(
    section =>
      section.routeName === routeName || section.activeOn?.includes(routeName)
  );

export const getSidebarAreaForRoute = routeName =>
  findRailSectionForRoute(routeName)?.name ?? null;
